import { useEffect, useRef, useState, type RefObject } from 'react';

/**
 * Nimmt den eigenen Tab auf und schiebt ihn an den Server.
 *
 * Gedacht fuer die Renderseite, die der Server selbst in einem unsichtbaren
 * Chrome oeffnet. Zwei Entscheidungen stecken darin:
 *
 * **Das Bild kommt aus `getDisplayMedia` auf den eigenen Tab**, nicht aus
 * einem Canvas. Damit landet genau das im Stream, was auch ein Zuschauer
 * sieht -- Hauptbild, kleines Fenster, Ticker, Bauchbinde -- ohne dass die
 * Einblendungen ein zweites Mal fuer ein Canvas nachgebaut werden muessten.
 * Chrome waehlt den Tab ueber `--auto-select-tab-capture-source-by-title`
 * anhand des Seitentitels aus; ohne diesen Schalter gaebe es einen Dialog,
 * den im unsichtbaren Browser niemand bedienen kann.
 *
 * **Der Ton kommt aus dem Web-Audio-Graph**, nicht aus der Tab-Aufnahme.
 * Tab-Audio liefert Chrome je nach Betriebssystem gar nicht, und der Umweg
 * ueber einen eigenen Mixer hat einen zweiten Vorteil: der Zielknoten
 * existiert von Anfang an mit einer Tonspur. Damit steht die Spurliste des
 * `MediaRecorder` fest, und die Regie kann waehrend der Sendung beliebig
 * umschneiden, ohne dass die Aufnahme neu starten muesste.
 */
export interface RecorderParams {
  /** WebSocket-Adresse des Servers, inklusive job und token. */
  ingestUrl: string;
  fps: number;
  videoKbps: number;
  audioKbps: number;
}

export type RecorderPhase = 'idle' | 'starting' | 'recording' | 'error';

/** Haeppchengroesze in ms. Klein genug fuer wenig Latenz, grosz genug, dass
 *  nicht jedes Bild eine eigene WebSocket-Nachricht wird. */
const TIMESLICE_MS = 250;

const MIME_CANDIDATES = [
  'video/webm;codecs=vp8,opus',
  'video/webm;codecs=vp9,opus',
  'video/webm',
];

/** `preferCurrentTab` ist Chrome-spezifisch und steht nicht in den DOM-Typen. */
interface CurrentTabConstraints extends DisplayMediaStreamOptions {
  preferCurrentTab?: boolean;
}

/** Tonmischer der laufenden Aufnahme: Kontext plus der Knoten, in den gemischt wird. */
export interface AudioMixer {
  context: AudioContext;
  target: AudioNode;
}

export interface TabRecorder {
  phase: RecorderPhase;
  error: string | null;
  /** Steht, sobald die Aufnahme laeuft -- hier haengt die Programm-Tonspur rein. */
  mixer: AudioMixer | null;
}

export function useTabRecorder(params: RecorderParams | null): TabRecorder {
  const [phase, setPhase] = useState<RecorderPhase>('idle');
  const [error, setError] = useState<string | null>(null);
  const [mixer, setMixer] = useState<AudioMixer | null>(null);
  const startedRef = useRef(false);
  /*
   * Die Parameter stehen in der URL und aendern sich nie -- der Effekt haengt
   * deshalb an der Adresse als Zeichenkette, nicht am Objekt. Wuerde eine
   * neue Objekt-Identitaet den Effekt erneut auslaufen lassen, liefe sein
   * Aufraeumteil, und `startedRef` verhinderte den Neustart: die Aufnahme
   * waere still tot.
   */
  const paramsRef = useRef(params);
  paramsRef.current = params;
  const ingestUrl = params?.ingestUrl ?? null;

  useEffect(() => {
    const params = paramsRef.current;
    if (!params || !ingestUrl || startedRef.current) return;
    startedRef.current = true;

    let socket: WebSocket | null = null;
    let recorder: MediaRecorder | null = null;
    let display: MediaStream | null = null;
    let audio: AudioContext | null = null;
    let disposed = false;

    const fail = (message: string) => {
      if (disposed) return;
      console.error('[render]', message);
      setError(message);
      setPhase('error');
      // Der Server merkt am Abbruch der WebSocket, dass er neu aufbauen muss.
      socket?.close(1011, 'recorder failed');
    };

    const run = async () => {
      setPhase('starting');

      audio = new AudioContext({ sampleRate: 48_000 });
      // Im unsichtbaren Chrome laeuft die Autoplay-Sperre nicht, ein resume
      // kostet aber nichts und rettet den Fall eines echten Browsers.
      await audio.resume().catch(() => undefined);
      const destination = audio.createMediaStreamDestination();

      /*
       * Stille Dauerquelle -- und die ist keine Kosmetik, sondern der
       * Unterschied zwischen "sendet" und "sendet nichts".
       *
       * Solange nichts an den Zielknoten angeschlossen ist, liefert seine
       * Tonspur ueberhaupt keine Samples. Der MediaRecorder wartet dann auf
       * Ton, den es nie gibt, und gibt *gar nichts* heraus -- auch kein Bild.
       * Genau dieser Fall ist der Normalzustand beim Start: da hat die Regie
       * oft noch keine Kamera aufs Hauptbild gelegt. Ein Knoten mit dem Pegel
       * 0 haelt die Spur am Laufen, ohne hoerbar zu sein.
       */
      const silence = audio.createConstantSource();
      silence.offset.value = 0;
      silence.connect(destination);
      silence.start();

      /*
       * Bremse vor dem Ausgang -- dieselbe wie im Mischer der Regie
       * (audioMix.ts). Hauptbild und kleines Fenster laufen gleichzeitig
       * hinein, und zwei laute Quellen addieren sich ueber Vollpegel; das
       * knackt erst im AAC bei YouTube und Twitch. Darunter greift sie nicht.
       */
      const bus = audio.createDynamicsCompressor();
      bus.threshold.value = -6;
      bus.knee.value = 6;
      bus.ratio.value = 12;
      bus.attack.value = 0.003;
      bus.release.value = 0.25;
      bus.connect(destination);

      if (disposed) return;
      setMixer({ context: audio, target: bus });

      display = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: params.fps },
        audio: false,
        preferCurrentTab: true,
      } as CurrentTabConstraints);
      if (disposed) return;

      const mixed = new MediaStream([...display.getVideoTracks(), ...destination.stream.getAudioTracks()]);
      const mimeType = MIME_CANDIDATES.find((m) => MediaRecorder.isTypeSupported(m));
      if (!mimeType) return fail('Dieser Browser kann kein WebM aufnehmen.');

      socket = new WebSocket(params.ingestUrl);
      socket.binaryType = 'arraybuffer';
      socket.onerror = () => fail('Die Verbindung zum Server ist fehlgeschlagen.');

      socket.onopen = () => {
        if (disposed) return;
        recorder = new MediaRecorder(mixed, {
          mimeType,
          videoBitsPerSecond: params.videoKbps * 1000,
          audioBitsPerSecond: params.audioKbps * 1000,
        });
        recorder.ondataavailable = (event) => {
          if (!event.data.size || socket?.readyState !== WebSocket.OPEN) return;
          socket.send(event.data);
        };
        recorder.onerror = () => fail('Die Aufnahme ist abgebrochen.');
        recorder.start(TIMESLICE_MS);
        setPhase('recording');
      };

      // Endet die Tab-Aufnahme, ist der Job nicht mehr zu retten -- der
      // Server baut ihn dann komplett neu auf.
      display.getVideoTracks()[0]?.addEventListener('ended', () => fail('Die Tab-Aufnahme wurde beendet.'));
    };

    void run().catch((err: unknown) =>
      fail(err instanceof Error ? err.message : 'Die Aufnahme konnte nicht starten.'),
    );

    return () => {
      disposed = true;
      if (recorder && recorder.state !== 'inactive') recorder.stop();
      display?.getTracks().forEach((t) => t.stop());
      socket?.close(1000, 'done');
      void audio?.close().catch(() => undefined);
    };
  }, [ingestUrl]);

  return { phase, error, mixer };
}

/**
 * Haengt die Tonspur eines Streams in den Mischer -- und wieder heraus, wenn
 * die Regie umschneidet. `audible` ist das Master-Stumm der Regie: die Spur
 * bleibt gesteckt, der Pegel geht auf null.
 *
 * Das versteckte <audio> ist kein Schoenheitsfehler, sondern Pflicht: Chrome
 * liefert die Tonspur einer WebRTC-Gegenstelle erst dann an Web Audio, wenn
 * sie an einem Medienelement haengt. Stumm ist es, weil der Ton in die
 * Aufnahme gehoert und nicht in die Lautsprecher des Servers.
 */
export function useMixedAudio(mixer: AudioMixer | null, stream: MediaStream | null, audible = true) {
  /*
   * Der Pegel liegt in einem eigenen Knoten und nicht im Auf- und Abbauen der
   * Verbindung. Der Grund ist das Master-Stumm der Regie: das wird mitten in
   * der Sendung geschaltet, und ein abgerissener und neu gesteckter
   * Mischereingang gibt genau dabei ein Knacken. Ein Pegel, der in 20 ms
   * faellt, klingt wie ein Fader.
   */
  const gainRef = useRef<GainNode | null>(null);
  const audibleRef = useRef(audible);
  audibleRef.current = audible;

  useEffect(() => {
    const gain = gainRef.current;
    const context = mixer?.context;
    if (!gain || !context) return;
    gain.gain.cancelScheduledValues(context.currentTime);
    gain.gain.setTargetAtTime(audible ? 1 : 0, context.currentTime, 0.02);
  }, [audible, mixer]);

  useEffect(() => {
    if (!mixer || !stream) return;
    const keepAlive = new Audio();
    keepAlive.srcObject = stream;
    keepAlive.muted = true;
    void keepAlive.play().catch(() => undefined);

    let source: MediaStreamAudioSourceNode | null = null;
    /*
     * `ontrack` feuert pro Spur, und Bild kommt oft vor Ton -- am selben
     * MediaStream-Objekt. Fuer React ist das keine Aenderung, dieser Effekt
     * liefe also nicht erneut. Deshalb zusaetzlich auf `addtrack` hoeren,
     * sonst geht der Stream stumm raus, wenn der Ton eine Spur zu spaet kommt.
     */
    let gain: GainNode | null = null;
    const attach = () => {
      if (source || stream.getAudioTracks().length === 0) return;
      try {
        const node = mixer.context.createMediaStreamSource(stream);
        gain = mixer.context.createGain();
        // Wird gerade stumm geschaltet, faengt die Quelle auch stumm an --
        // sonst blitzt beim Umschneiden ein Ton auf, den niemand wollte.
        gain.gain.value = audibleRef.current ? 1 : 0;
        node.connect(gain);
        gain.connect(mixer.target);
        gainRef.current = gain;
        source = node;
      } catch (err) {
        console.warn('[render] Ton konnte nicht gemischt werden', err);
      }
    };
    attach();
    stream.addEventListener('addtrack', attach);

    return () => {
      stream.removeEventListener('addtrack', attach);
      source?.disconnect();
      gain?.disconnect();
      if (gainRef.current === gain) gainRef.current = null;
      keepAlive.srcObject = null;
    };
  }, [mixer, stream]);
}

/**
 * Haengt den Ton eines Medienelements in den Mischer -- gebraucht fuer den
 * Werbespot, der als gewoehnliches <video> mit Datei laeuft und nicht als
 * WebRTC-Stream.
 *
 * `createMediaElementSource` statt `captureStream`: der Ton geht dann
 * *nur* noch in den Graph und nicht mehr in die Lautsprecher des Servers,
 * und das Element darf dafuer laut gestellt sein. Ein stummes Element
 * lieferte auch dem Mischer nur Stille. Die Verbindung laesst sich je
 * Element genau einmal herstellen -- deshalb haengt sie am Mischer, nicht am
 * jeweiligen Spot.
 */
export function useMixedElement(
  mixer: AudioMixer | null,
  elementRef: RefObject<HTMLMediaElement | null>,
  audible = true,
) {
  const gainRef = useRef<GainNode | null>(null);
  const audibleRef = useRef(audible);
  audibleRef.current = audible;

  useEffect(() => {
    const gain = gainRef.current;
    const context = mixer?.context;
    if (!gain || !context) return;
    gain.gain.cancelScheduledValues(context.currentTime);
    gain.gain.setTargetAtTime(audible ? 1 : 0, context.currentTime, 0.02);
  }, [audible, mixer]);

  useEffect(() => {
    const element = elementRef.current;
    if (!mixer || !element) return;
    let source: MediaElementAudioSourceNode;
    try {
      source = mixer.context.createMediaElementSource(element);
    } catch (err) {
      console.warn('[render] Spot-Ton konnte nicht gemischt werden', err);
      return;
    }
    const gain = mixer.context.createGain();
    gain.gain.value = audibleRef.current ? 1 : 0;
    source.connect(gain);
    gain.connect(mixer.target);
    gainRef.current = gain;
    return () => {
      source.disconnect();
      gain.disconnect();
      if (gainRef.current === gain) gainRef.current = null;
    };
  }, [mixer, elementRef]);
}
